import "./ActiveOrders.css";
import React, { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useOrders } from "../context/OrdersContext";
import { useDisplayConfig } from "../context/DisplayConfigContext";
import Settings from "./Settings";
import { useSwipeable } from "react-swipeable";
import { useSocket } from "../context/SocketContext";
import { useIp } from "../context/IpContext";

// ── Audio (module-level — survives component remounts) ────────────────────────
// Uses the Web Audio API for reliable ring playback on iOS and Android.
// No keepalive interval — relies on visibilitychange to resume on wake.
// If iOS blocks auto-resume (requires gesture), the banner is shown again.

let audioCtx = null; // singleton AudioContext
let ringBuffer = null; // decoded ring.mp3 PCM data
let ringUnlocked = false; // true once context is running and buffer is ready

async function unlockAudio() {
  if (ringUnlocked) return;
  try {
    if (!audioCtx) {
      audioCtx = new (window.AudioContext || window.webkitAudioContext)();
    }
    await audioCtx.resume();

    if (!ringBuffer) {
      const res = await fetch("/sounds/ring.mp3");
      const raw = await res.arrayBuffer();
      ringBuffer = await audioCtx.decodeAudioData(raw);
    }

    ringUnlocked = true;
  } catch (err) {
    console.warn("Audio init failed:", err);
  }
}

function playRing() {
  if (audioCtx && ringBuffer) {
    // Resume in case iOS suspended between unlock and now (e.g. screen lock)
    if (audioCtx.state === "suspended") audioCtx.resume();
    const src = audioCtx.createBufferSource();
    src.buffer = ringBuffer;
    src.connect(audioCtx.destination);
    src.start(0);
  } else {
    // Fallback for browsers where Web Audio API setup failed
    new Audio("/sounds/ring.mp3")
      .play()
      .catch((e) => console.warn("Ring blocked:", e));
  }
}

const computeTotals = (orders, doneItems) => {
  const totals = {};

  for (const order of orders) {
    for (let i = 0; i < order.items.length; i++) {
      const item = order.items[i];
      const qty = Number(item.quantity) || 1;

      if (!doneItems.has(item.uid)) {
        totals[item.name] = (totals[item.name] || 0) + qty;
      }
    }
  }

  return totals;
};

export default function ActiveOrders() {
  const { type } = useParams();
  const {
    allOrders,
    setAllOrders,
    pageIndex,
    setPageIndex,
    ordersPerPage,
    fontSize,
  } = useOrders();
  const { displayConfigs } = useDisplayConfig();
  const displayConfig = displayConfigs?.find((d) => d.id === type);
  const displayName = displayConfig?.name ?? type;
  const ringEnabled = displayConfig?.ringEnabled ?? false;

  const [itemTotals, setItemTotals] = useState({});
  const [doneItems, setDoneItems] = useState(new Set());
  const [showSettings, setShowSettings] = useState(false);
  const [paginatedOrders, setPaginatedOrders] = useState([]);
  const [audioUnlocked, setAudioUnlocked] = useState(ringUnlocked);
  const { socket } = useSocket();
  const navigate = useNavigate();
  const { ipAddress, port } = useIp();

  // Go to previous page (with boundary check)
  const goToPrevPage = () => {
    setPageIndex((prev) => Math.max(0, prev - 1));
  };

  // Go to next page (with boundary check)
  const goToNextPage = () => {
    const maxPage = Math.floor((allOrders.length - 1) / ordersPerPage);
    setPageIndex((prev) => Math.min(maxPage, prev + 1));
  };

  const pageHandlers = useSwipeable({
    onSwipedLeft: () => goToNextPage(),
    onSwipedRight: () => goToPrevPage(),
    preventDefaultTouchmoveEvent: true,
    trackMouse: true, // Enable mouse tracking for desktop
  });

  // Fetch existing active orders via REST once on mount, then rely solely on
  // socket events for updates. initializedRef prevents a re-fetch if the
  // component re-renders (e.g. after a settings change).
  // Browsers block audio until the user interacts with the page.
  // Register the unlock listener immediately on mount (no ringEnabled gate) so
  // the user's first tap always unlocks audio regardless of config load timing.
  // audioUnlocked drives a visible banner so kitchen staff know to tap the screen.
  // On the first user gesture (tap/click), initialise the Web Audio API.
  // Must happen inside a gesture handler — iOS blocks AudioContext creation otherwise.
  useEffect(() => {
    // Listeners stay permanent so they can re-unlock if iOS suspends the
    // context and forces the banner to reappear after a long idle period.
    const unlock = () => {
      unlockAudio().then(() => {
        if (ringUnlocked) setAudioUnlocked(true);
      });
    };
    window.addEventListener("touchstart", unlock, { passive: true });
    window.addEventListener("click", unlock);
    return () => {
      window.removeEventListener("touchstart", unlock);
      window.removeEventListener("click", unlock);
    };
  }, []);

  // When the tablet wakes or the browser comes back to foreground, try to
  // resume the AudioContext. If iOS blocks it (requires a fresh gesture),
  // reset ringUnlocked and show the "tap anywhere" banner so staff know
  // to tap the screen before the next order arrives.
  useEffect(() => {
    const handleVisible = async () => {
      if (document.visibilityState !== "visible") return;
      if (!audioCtx || audioCtx.state !== "suspended") return;
      try {
        await audioCtx.resume();
        if (audioCtx.state === "suspended") throw new Error("still suspended");
      } catch {
        ringUnlocked = false;
        setAudioUnlocked(false);
      }
    };
    document.addEventListener("visibilitychange", handleVisible);
    return () =>
      document.removeEventListener("visibilitychange", handleVisible);
  }, []);

  // Fetches the current active orders from the REST API and replaces local
  // state. Used on mount and whenever the page wakes from sleep so that any
  // orders received while the device was off-screen are not missed.
  const fetchAndSyncOrders = useCallback(() => {
    if (!ipAddress) return;
    fetch(`http://${ipAddress}:${port}/api/${type}/active-orders`)
      .then((res) => res.json())
      .then((orders) => {
        const sorted = [...orders].sort((a, b) => {
          if (a.reactivated_at && !b.reactivated_at) return -1;
          if (!a.reactivated_at && b.reactivated_at) return 1;
          if (a.reactivated_at && b.reactivated_at) {
            return new Date(b.reactivated_at) - new Date(a.reactivated_at);
          }
          return new Date(a.created_at) - new Date(b.created_at);
        });
        // Ring once if new orders appeared while the tablet was asleep.
        // knownOrderUuidsRef is null on the very first load — skip ring then.
        if (knownOrderUuidsRef.current !== null) {
          const hasNew = sorted.some(
            (o) => !knownOrderUuidsRef.current.has(o.uuid),
          );
          if (hasNew && ringEnabledRef.current) playRing();
        }
        knownOrderUuidsRef.current = new Set(sorted.map((o) => o.uuid));
        setAllOrders(sorted);
        setPageIndex(0);
      })
      .catch((err) => console.error("Failed to sync orders", err));
  }, [ipAddress, port, type, setAllOrders, setPageIndex]);

  // Ref so the socket handler always reads the latest ringEnabled value without
  // being a dependency — prevents socket listener teardown/re-registration on
  // every config load, which would create a gap where events are missed.
  const ringEnabledRef = useRef(ringEnabled);
  useEffect(() => {
    ringEnabledRef.current = ringEnabled;
  }, [ringEnabled]);

  // Tracks order UUIDs seen at the last REST sync. null = first load (don't ring).
  const knownOrderUuidsRef = useRef(null);

  // Initial load on mount
  const initializedRef = useRef(false);
  useEffect(() => {
    if (!ipAddress || initializedRef.current) return;
    initializedRef.current = true;
    fetchAndSyncOrders();
  }, [ipAddress, fetchAndSyncOrders]);

  // Re-sync when the tablet wakes from sleep (page becomes visible again)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        fetchAndSyncOrders();
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () =>
      document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, [fetchAndSyncOrders]);

  // Ref so the reconnect handler always calls the latest fetchAndSyncOrders
  // without it being an effect dependency — its identity changes when the
  // server IP loads, and re-registering the socket listeners at that moment
  // would open a gap where events are missed.
  const fetchAndSyncOrdersRef = useRef(fetchAndSyncOrders);
  useEffect(() => {
    fetchAndSyncOrdersRef.current = fetchAndSyncOrders;
  }, [fetchAndSyncOrders]);

  // Fetch orders via socket (real-time)
  useEffect(() => {
    if (!socket) return;

    // Join on mount and on every reconnect (sleep drops the room membership
    // on the server side, so we must re-join whenever the socket comes back)
    socket.emit("join_room", type);
    const handleReconnect = () => {
      socket.emit("join_room", type);
      // Orders broadcast while this socket was down went into an empty room
      // and are never re-sent, so a reconnect must re-sync via REST — without
      // this, a Wi-Fi blip at the wrong moment hides an order until the page
      // is reloaded or woken. Rings if the sync reveals unseen orders.
      fetchAndSyncOrdersRef.current();
    };
    socket.on("connect", handleReconnect);

    // eventType is "new" (appended to end) or "reactivate" (prepended to front)
    const handleOrder = (order, eventType) => {
      // Ignore orders meant for a different display
      if (order.category !== type) return;

      console.log("Received order:", order);

      // Add a 'served' field to each line item
      const updatedOrder = {
        ...order,
        items: Array.isArray(order.items)
          ? order.items.map((item) => ({ ...item, served: false }))
          : [], // default to empty array if items is undefined
      };

      setAllOrders((prevOrders) => {
        const index = prevOrders.findIndex((o) => o.uuid === updatedOrder.uuid);
        let newOrders;
        if (index !== -1) {
          newOrders = [...prevOrders];
          newOrders[index] = updatedOrder; // replace existing order
        } else {
          newOrders =
            eventType === "new"
              ? [...prevOrders, updatedOrder] // Add new order to the end
              : [updatedOrder, ...prevOrders]; // Add reactivated order to the front
        }

        // Reset pageIndex if previously empty or order prepended
        if (prevOrders.length === 0 || eventType === "reactivate") {
          setPageIndex(0);
        }

        return newOrders;
      });

      // Track this UUID so the next REST sync doesn't ring for it again.
      // Without this, a socket-delivered order isn't in knownOrderUuidsRef and
      // the next fetchAndSyncOrders call would treat it as new and ring twice.
      knownOrderUuidsRef.current?.add(updatedOrder.uuid);

      // Play ring outside the state updater — side effects must not live inside
      // state updater functions (React can invoke them more than once).
      // Uses ringEnabledRef so this handler doesn't need ringEnabled as a dep,
      // which would cause listener teardown/re-registration on config load.
      if (ringEnabledRef.current) {
        playRing();
      }
    };

    const handleCompletedOrder = (order) => {
      setAllOrders((prevOrders) =>
        prevOrders.filter((o) => o.uuid !== order.uuid),
      );
    };

    const handleNewOrder = (order) => handleOrder(order, "new");
    const handleReactivatedOrder = (order) => handleOrder(order, "reactivate");

    socket.on("new_order", handleNewOrder);
    socket.on("reactivate_order", handleReactivatedOrder);
    socket.on("order_completed", handleCompletedOrder);

    // Pass specific handler references so only this component's listeners are
    // removed — socket.off("event") with no reference would strip listeners
    // registered by other mounted components (e.g. CompletedOrders) too.
    return () => {
      socket.off("connect", handleReconnect);
      socket.off("new_order", handleNewOrder);
      socket.off("reactivate_order", handleReactivatedOrder);
      socket.off("order_completed", handleCompletedOrder);
    };
  }, [socket, type, setAllOrders]);

  // Update orders per page
  useEffect(() => {
    if (allOrders.length === 0) {
      setPaginatedOrders([]);
      return;
    }

    const maxPage = Math.floor((allOrders.length - 1) / ordersPerPage);
    const safePageIndex = Math.min(pageIndex, maxPage);

    setPaginatedOrders(
      allOrders.slice(
        safePageIndex * ordersPerPage,
        safePageIndex * ordersPerPage + ordersPerPage,
      ),
    );
  }, [allOrders, pageIndex, setPaginatedOrders, ordersPerPage]);

  // When allOrders or ordersPerPage changes, reset pageIndex if needed
  useEffect(() => {
    // skip clamping for empty list
    if (allOrders.length === 0 || ordersPerPage <= 0) return;

    const maxPage = Math.floor((allOrders.length - 1) / ordersPerPage);
    setPageIndex((prev) => Math.min(prev, maxPage));
  }, [allOrders, ordersPerPage, pageIndex, setPageIndex]);

  // Recompute totals anytime orders or doneItems change
  useEffect(() => {
    const newTotals = computeTotals(paginatedOrders, doneItems);
    setItemTotals(newTotals);
  }, [paginatedOrders, doneItems]);

  const toggleItemDone = (uuid, itemUid, itemIndex) => {
    // Move item to bottom
    setAllOrders((prev) =>
      prev.map((order) => {
        if (order.uuid !== uuid) return order;

        // Items before toggle
        const itemsBeforeToggle = order.items.filter(
          (_, idx) => idx !== itemIndex,
        );
        const toggledItem = order.items[itemIndex];

        return {
          ...order,
          items: [...itemsBeforeToggle, toggledItem],
        };
      }),
    );

    // Update doneItems Set to mark toggle state
    setDoneItems((prevDoneItems) => {
      const newSet = new Set(prevDoneItems);
      if (newSet.has(itemUid)) {
        newSet.delete(itemUid);
      } else {
        newSet.add(itemUid);
      }
      return newSet;
    });
  };

  // Remove entire order
  const dismissOrder = async (uuid, items) => {
    // Capture the order now for potential rollback before state is mutated
    const orderToRestore = allOrders.find((o) => o.uuid === uuid);

    // Optimistically remove from UI immediately so the screen responds at once
    setItemTotals((prevTotals) => {
      const newTotals = { ...prevTotals };
      for (const item of items) {
        const qty = Number(item.quantity) || 1;
        if (!doneItems.has(item.uid)) {
          newTotals[item.name] = Math.max((newTotals[item.name] || 0) - qty, 0);
        }
      }
      return newTotals;
    });

    setAllOrders((prevAllOrders) => {
      const updatedOrders = prevAllOrders.filter((o) => o.uuid !== uuid);
      const maxPage = Math.max(
        0,
        Math.floor((updatedOrders.length - 1) / ordersPerPage),
      );
      setPageIndex((prevPage) => Math.min(prevPage, maxPage));
      return updatedOrders;
    });

    // Confirm with the backend — restore the order if the request fails
    try {
      const res = await fetch(
        `http://${ipAddress}:${port}/api/complete-order`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ uuid }),
        },
      );
      if (!res.ok) throw new Error(`Server returned ${res.status}`);
    } catch (e) {
      console.error("Failed to mark order completed", e);
      if (orderToRestore) {
        setAllOrders((prev) => {
          if (prev.some((o) => o.uuid === uuid)) return prev; // already back (e.g. socket)
          return [...prev, orderToRestore];
        });
      }
    }
  };

  return (
    <div
      style={{
        display: "flex",
        height: "100vh",
        flexDirection: "row",
        gap: "16px",
        boxSizing: "border-box",
      }}
    >
      {/* Left Side: Item Totals */}
      <div className="item-totals-container">
        <div className="sidebar-header">
          <div className="display-type-badge">{displayName}</div>
          <h3 className="sidebar-title" style={{ fontSize: `${fontSize}%` }}>
            Items Ordered
          </h3>
        </div>
        <ul className="item-totals-list">
          {Object.entries(itemTotals).map(([name, qty]) => (
            <li
              key={name}
              className="item-total-row"
              style={{ fontSize: `${fontSize}%` }}
            >
              <span className="item-total-qty">{qty}</span>
              <span className="item-total-name">{name}</span>
            </li>
          ))}
          {Object.keys(itemTotals).length === 0 && (
            <li className="no-items-text" style={{ fontSize: `${fontSize}%` }}>
              — no items —
            </li>
          )}
        </ul>
        {/* Sound unlock prompt — only on ring-enabled displays, hidden once user has tapped */}
        {!audioUnlocked && ringEnabled && (
          <div className="audio-unlock-prompt">
            <span className="audio-unlock-bell">🔔</span>
            <span>Tap anywhere to enable sound alerts</span>
          </div>
        )}

        {/* Bottom actions: gear + completed button, flex-aligned */}
        <div className="bottom-actions">
          <button
            onClick={() => setShowSettings(true)}
            className="settings-button"
            aria-label="Settings"
            title="Settings"
            style={{ fontSize: `${fontSize + 100}%` }}
          >
            ⚙️
          </button>
          <button
            onClick={() => navigate(`/completed/${type}`)}
            className="completed-button"
            style={{ fontSize: `${fontSize}%` }}
          >
            View Completed Orders
          </button>
        </div>

        {/* Settings */}
        {showSettings && (
          <Settings onClose={() => setShowSettings(false)} type={type} />
        )}
      </div>

      {/* Right side: Order cards */}
      <div {...pageHandlers} className="active-orders-container">
        {paginatedOrders.length === 0 && (
          <div className="no-orders-state">No Active Orders</div>
        )}
        {paginatedOrders.map((order) => (
          <div className="active-order" key={order.uuid}>
            {/* Top Section: Order Header */}
            <div
              className={`active-order-header ${order.type === "takeaway" ? "takeaway" : "dinein"}`}
              style={{ fontSize: `${fontSize}%` }}
              onClick={() => dismissOrder(order.uuid, order.items)}
            >
              {order.reactivated_at && (
                <div className="reactivated-badge">↺ Reactivated</div>
              )}
              <div className="order-number">Order #{order.order_name}</div>
              <div className="active-order-meta-row">
                <span>{formatTime(order.created_at)}</span>
                <span className="active-order-type-label">
                  {order.type === "takeaway" ? "Takeaway" : "Dine-In"}
                </span>
              </div>
            </div>

            {/* Bottom Section: Items */}
            <div
              className="active-order-items"
              style={{ fontSize: `${fontSize}%` }}
            >
              <ul style={{ paddingLeft: "1rem", margin: 0 }}>
                {order.items?.map((item, i) => {
                  const isDone = doneItems.has(item.uid);
                  return (
                    <li
                      key={item.uid}
                      onClick={() => toggleItemDone(order.uuid, item.uid, i)}
                      style={{
                        textDecoration: isDone ? "line-through" : "none",
                        opacity: isDone ? 0.5 : 1,
                        cursor: "pointer",
                        fontWeight: "bold",
                        fontSize: `${fontSize}%`,
                        marginBottom: "0.5rem",
                        listStyle: "none",
                      }}
                    >
                      {/* Quantity Box */}
                      <span
                        className={`item-qty ${
                          Number(item.quantity) === 1
                            ? "qty-default"
                            : "qty-warning"
                        }`}
                        style={{ fontSize: `${fontSize}%` }}
                      >
                        {item.quantity}
                      </span>
                      {/* Item Name */}
                      <span>{item.name}</span>

                      {/* Modifiers + Variations + Note */}
                      {(item.modifiers || item.variation_name || item.note) && (
                        <ul
                          style={{
                            listStyle: "none",
                            fontSize: `${fontSize}%`,
                            marginTop: "4px",
                            marginLeft: "1rem",
                          }}
                        >
                          {/* Note */}
                          {item.note && item.note.trim() !== "" && (
                            <li style={{ color: "red", fontWeight: "bold" }}>
                              NOTE: {item.note}
                            </li>
                          )}

                          {/* Variations */}
                          {item.variation_name &&
                            item.variation_name.trim() !== "" &&
                            item.variation_name.trim().toLowerCase() !==
                              "regular" && (
                              <li style={{ color: "#555" }}>
                                + {item.variation_name}
                              </li>
                            )}

                          {/* Modifiers */}
                          {item.modifiers &&
                            item.modifiers.map((mod) => (
                              <li
                                key={`${item.uid}-${mod.name}`}
                                style={{ color: "#555" }}
                              >
                                + {mod.name}
                                {mod.quantity > 1 ? (
                                  <span style={{ color: "red" }}>
                                    {" "}
                                    x {mod.quantity}
                                  </span>
                                ) : (
                                  ""
                                )}
                              </li>
                            ))}
                        </ul>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>
        ))}

        {/* Page Indicator */}
        <div className="page-indicator">
          {Array.from({
            length: Math.ceil(allOrders.length / ordersPerPage),
          }).map((_, index) => (
            <div
              key={index}
              className={`dot ${index === pageIndex ? "active" : ""}`}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

function formatTime(timestamp) {
  const date = new Date(timestamp);
  const time = date
    .toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
    })
    .toUpperCase();

  return time;
}
